import { useMemo, useRef, useState } from 'react'
import type { EstoqueRow } from '../../hooks/useEstoqueAnalytics'
import KpiCard from './KpiCard'
import BarChart from './charts/BarChart'
import { fmtNum, fmtPct, fmtDays } from '../../lib/format'
import { isRupturaAcionavel } from '../../lib/replenishment'
import { kpiMeta } from '../../lib/kpiMeta'

interface Props { rows: EstoqueRow[] }

const COVERAGE_LEAD_DAYS = 15

export default function OperacionalPanel({ rows }: Props) {
  const stats = useMemo(() => {
    const withStock = rows.filter(r => r.current_stock > 0)
    const total = rows.length

    // ── Ruptura por curva ABC ─────────────────────────────────────────
    // Régua única (ao vivo): saldo ≤ 0 sem motivo operacional — ver isRupturaAcionavel.
    const rupturaRows = rows.filter(r => isRupturaAcionavel(r.current_stock, r.kanban_status))
    const rupturaTotal = rupturaRows.length
    const rupturaByAbc = { A: 0, B: 0, C: 0, null: 0 }
    const totalByAbc = { A: 0, B: 0, C: 0, null: 0 }
    for (const r of rows) {
      const k = (r.abc_curve ?? 'null') as keyof typeof rupturaByAbc
      totalByAbc[k]++
      if (isRupturaAcionavel(r.current_stock, r.kanban_status)) rupturaByAbc[k]++
    }

    // ── Distribuição de cobertura ─────────────────────────────────────
    const coverageBuckets = [
      { label: 'Ruptura', count: 0, color: '#ef4444' },
      { label: '1–15d', count: 0, color: '#f97316' },
      { label: '15–30d', count: 0, color: '#eab308' },
      { label: '30–60d', count: 0, color: '#22d3ee' },
      { label: '60–90d', count: 0, color: '#34d399' },
      { label: '>90d', count: 0, color: '#818cf8' },
    ]
    for (const r of rows) {
      if (isRupturaAcionavel(r.current_stock, r.kanban_status)) { coverageBuckets[0].count++; continue }
      const d = r.coverage_days ?? 0
      if (d < 15) coverageBuckets[1].count++
      else if (d < 30) coverageBuckets[2].count++
      else if (d < 60) coverageBuckets[3].count++
      else if (d < 90) coverageBuckets[4].count++
      else coverageBuckets[5].count++
    }

    // ── Giro do estoque ───────────────────────────────────────────────
    // giro estimado = receita no período / valor em estoque (período = 6 meses → anualizamos ×2)
    let totalRevenue = 0, totalValue = 0, totalMargin = 0
    for (const r of rows) {
      if (r.revenue_window != null) totalRevenue += r.revenue_window
      if (r.margin_value   != null) totalMargin  += r.margin_value
      totalValue += (r.current_stock ?? 0) * (r.cost_avg ?? 0)
    }
    const giroSemestral = totalValue > 0 ? totalRevenue / totalValue : null
    const giroAnual = giroSemestral != null ? giroSemestral * 2 : null
    // giro real (a custo): CMV do período (receita − margem) anualizado ÷ valor em estoque
    const cmv = totalRevenue - totalMargin
    const giroRealCmv = totalValue > 0 ? (cmv * 2) / totalValue : null

    // Giro por curva
    const giroAbc: Record<string, { rev: number; val: number }> = { A: { rev: 0, val: 0 }, B: { rev: 0, val: 0 }, C: { rev: 0, val: 0 } }
    for (const r of rows) {
      if (!r.abc_curve || !(r.abc_curve in giroAbc)) continue
      if (r.revenue_window != null) giroAbc[r.abc_curve].rev += r.revenue_window
      giroAbc[r.abc_curve].val += (r.current_stock ?? 0) * (r.cost_avg ?? 0)
    }

    // ── Estoque crítico (cobertura < lead time proxy) ─────────────────
    const critico = withStock
      .filter(r => !isRupturaAcionavel(r.current_stock, r.kanban_status) && (r.coverage_days ?? 999) < COVERAGE_LEAD_DAYS)
      .sort((a, b) => (a.coverage_days ?? 0) - (b.coverage_days ?? 0))

    return { total, rupturaRows, rupturaTotal, rupturaByAbc, totalByAbc, coverageBuckets, giroAnual, giroRealCmv, giroAbc, critico }
  }, [rows])

  const rupturaRate = stats.total > 0 ? (stats.rupturaTotal / stats.total) * 100 : 0
  const rupturaAPct = stats.totalByAbc.A > 0 ? (stats.rupturaByAbc.A / stats.totalByAbc.A) * 100 : null

  // Metas dos KPIs (régua única → tom do card + selo). Limiares vêm do texto "analysis".
  const rupturaMeta = kpiMeta({ value: rupturaRate, sentido: 'max', verde: 5, amarelo: 15, format: v => `${v}%` })
  const criticoMeta = kpiMeta({ value: stats.critico.length, sentido: 'max', verde: 0, amarelo: 9, format: v => `${v}` })
  const giroAnualMeta = kpiMeta({ value: stats.giroAnual, sentido: 'min', verde: 8, amarelo: 4, format: v => `${v}×` })
  const giroRealMeta = kpiMeta({ value: stats.giroRealCmv, sentido: 'min', verde: 6, amarelo: 3, format: v => `${v}×` })
  const rupturaAMeta = kpiMeta({ value: rupturaAPct, sentido: 'max', verde: 0, amarelo: 0, format: v => `${v}%` })

  // Card ativo dirige a tabela detalhada (contagens → lista filtrada).
  const [activeCard, setActiveCard] = useState<'ruptura' | 'critico' | 'rupturaA' | null>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  function selectCard(key: 'ruptura' | 'critico' | 'rupturaA') {
    const next = activeCard === key ? null : key
    setActiveCard(next)
    if (next) tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Lista exibida: default = estoque crítico (comportamento atual).
  const list = useMemo(() => {
    if (activeCard === 'ruptura') return { title: 'Em ruptura', rows: stats.rupturaRows }
    if (activeCard === 'rupturaA') return { title: 'Em ruptura — curva A', rows: stats.rupturaRows.filter(r => r.abc_curve === 'A') }
    return { title: `Estoque crítico — cobertura < ${COVERAGE_LEAD_DAYS}d`, rows: stats.critico }
  }, [activeCard, stats])

  return (
    <div className="flex flex-col gap-6">
      {/* ── KPIs rápidos ──────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 [&>*]:w-[220px]">
        <KpiCard
          label="Taxa de ruptura"
          value={fmtPct(rupturaRate)}
          tone={rupturaMeta.tone}
          meta={{ status: rupturaMeta.status, label: rupturaMeta.metaLabel }}
          hint="clique para listar"
          onClick={() => selectCard('ruptura')}
          active={activeCard === 'ruptura'}
          analysis="% de SKUs zerados. <5% bom · 5–15% atenção · >15% crítico."
        />
        <KpiCard
          label="Estoque crítico"
          value={stats.critico.length}
          tone={criticoMeta.tone}
          meta={{ status: criticoMeta.status, label: criticoMeta.metaLabel }}
          hint="clique para listar"
          onClick={() => selectCard('critico')}
          active={activeCard === 'critico'}
          analysis="SKUs com cobertura < 15 dias. Priorize a compra antes de zerar."
        />
        <KpiCard
          label="Giro anual estimado"
          value={stats.giroAnual != null ? `${stats.giroAnual.toFixed(1)}×` : '—'}
          tone={giroAnualMeta.tone}
          meta={{ status: giroAnualMeta.status, label: giroAnualMeta.metaLabel }}
          hint="receita 6m ÷ valor estoque × 2"
          analysis="Quantas vezes o estoque gira no ano. ≥8× bom · 4–8× atenção · <4× baixo (estoque parado). Varia por ramo — acompanhe a tendência."
        />
        <KpiCard
          label="Giro real (CMV)"
          value={stats.giroRealCmv != null ? `${stats.giroRealCmv.toFixed(1)}×` : '—'}
          tone={giroRealMeta.tone}
          meta={{ status: giroRealMeta.status, label: giroRealMeta.metaLabel }}
          hint="CMV 6m ÷ valor estoque × 2"
          analysis="Giro a custo (custo das vendas ÷ estoque) — mais conservador que o estimado, que sai inflado pelo markup. ≥6× bom · 3–6× atenção · <3× baixo. Varia por ramo."
        />
        <KpiCard
          label="Ruptura curva A"
          value={rupturaAPct != null ? fmtPct(rupturaAPct) : '—'}
          tone={rupturaAMeta.tone}
          meta={{ status: rupturaAMeta.status, label: rupturaAMeta.metaLabel }}
          hint="clique para listar"
          onClick={() => selectCard('rupturaA')}
          active={activeCard === 'rupturaA'}
          analysis="% dos itens A (mais importantes) em ruptura. Ideal 0% — faltar um item A pesa mais."
        />
      </div>

      {/* ── Detalhamento ─────────────────────────────────────── */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">

        {/* Ruptura por curva */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Ruptura por curva ABC</h3>
          <BarChart
            horizontal
            bars={(['A', 'B', 'C'] as const).map(k => ({
              label: `Curva ${k}`,
              value: stats.totalByAbc[k] > 0 ? (stats.rupturaByAbc[k] / stats.totalByAbc[k]) * 100 : 0,
              color: k === 'A' ? '#ef4444' : k === 'B' ? '#f97316' : '#eab308',
            }))}
            formatValue={v => fmtPct(v)}
          />
          <p className="text-[11px] text-ink-faint">% de SKUs em ruptura por curva</p>
        </section>

        {/* Distribuição de cobertura */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Distribuição de cobertura</h3>
          <BarChart
            horizontal
            bars={stats.coverageBuckets.map(b => ({ label: b.label, value: b.count, color: b.color }))}
            formatValue={v => fmtNum(v)}
          />
          <p className="text-[11px] text-ink-faint">SKUs por faixa de dias de cobertura</p>
        </section>

        {/* Giro por curva */}
        <section className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Giro anual por curva</h3>
          <BarChart
            bars={(['A', 'B', 'C'] as const).map(k => ({
              label: `Curva ${k}`,
              value: stats.giroAbc[k].val > 0 ? (stats.giroAbc[k].rev / stats.giroAbc[k].val) * 2 : 0,
              color: k === 'A' ? '#22d3ee' : k === 'B' ? '#34d399' : '#818cf8',
            }))}
            height={96}
            formatValue={v => `${v.toFixed(1)}×`}
          />
          <p className="text-[11px] text-ink-faint">Giro anual estimado (receita 6m ÷ valor × 2)</p>
        </section>

        {/* Lista dirigida pelo card ativo (default: estoque crítico) */}
        <section ref={tableRef} className="bg-surface rounded-xl border border-line p-4 flex flex-col gap-3 md:col-span-2 lg:col-span-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{list.title}</h3>
            {activeCard && (
              <button
                onClick={() => setActiveCard(null)}
                className="text-[11px] px-2 py-0.5 rounded bg-sunken hover:bg-line text-ink-muted border border-line"
              >
                Limpar
              </button>
            )}
          </div>
          {list.rows.length === 0 ? (
            <p className="text-sm text-emerald-400">Nenhum SKU nesta lista.</p>
          ) : (
            <div className="overflow-y-auto max-h-[420px]">
              <table className="w-full text-sm mobile-stack-table">
                <thead className="sticky top-0 bg-surface">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-ink-faint border-b border-line">
                    <th className="pb-2 font-medium">SKU</th>
                    <th className="pb-2 font-medium">Produto</th>
                    <th className="pb-2 font-medium text-right">Saldo</th>
                    <th className="pb-2 font-medium text-right">Cobertura</th>
                    <th className="pb-2 font-medium text-center">Curva</th>
                  </tr>
                </thead>
                <tbody>
                  {(activeCard ? list.rows : list.rows.slice(0, 10)).map(r => (
                    <tr key={r.id} className="border-b border-line/50 last:border-0">
                      <td className="py-1.5 pr-4 text-ink-muted font-mono text-xs">{r.sku}</td>
                      <td className="py-1.5 pr-4 text-ink max-w-[200px] truncate">{r.name}</td>
                      <td className="py-1.5 pr-4 text-right text-ink-muted">{fmtNum(r.current_stock)}</td>
                      <td className="py-1.5 pr-4 text-right font-semibold text-orange-400">
                        {r.coverage_days != null ? fmtDays(r.coverage_days) : '—'}
                      </td>
                      <td className="py-1.5 text-center">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                          r.abc_curve === 'A' ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300' :
                          r.abc_curve === 'B' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' :
                          'bg-sunken text-ink-muted'
                        }`}>{r.abc_curve ?? '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!activeCard && list.rows.length > 10 && (
                <p className="text-[11px] text-ink-faint mt-2">+{list.rows.length - 10} SKUs adicionais</p>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
